import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AgenticFeatureRegistry, AgenticSettings } from '../AgenticFeatureRegistry';

export interface AgenticContextConfig {
  /**
   * Feature key for global features (e.g., 'analytics-dashboard')
   * Setting this clears applicationKey
   */
  featureKey?: string;
  
  /**
   * Application key for application-specific agents
   * Setting this clears featureKey
   */
  applicationKey?: string;
  
  /**
   * Assistant key for the assistant configuration
   */
  assistantKey?: string;
  
  /**
   * Agent ID override
   */
  agentId?: string;
  
  /**
   * Focus key for entity-specific context (e.g., dashboard ID, customer ID)
   */
  focusKey?: string;
  
  /**
   * Whether to clear context when component unmounts or route changes
   * Default: true
   */
  clearOnUnmount?: boolean;
  
  /**
   * Route patterns that should preserve this context (even if clearOnUnmount is true)
   * Useful for nested routes or modals
   */
  preserveOnRoutes?: string[];
  
  /**
   * Whether this is a contextual control (like AiInsightCard) that manages its own isolated context
   * When true, context is only active while component is mounted
   * Default: false
   */
  isContextualControl?: boolean;
}

/**
 * Hook for managing AI chat context with automatic cleanup
 * 
 * Usage examples:
 * 
 * 1. Feature-based context (clears on navigation):
 * ```tsx
 * useAgenticContext({
 *   featureKey: 'analytics-dashboard',
 *   clearOnUnmount: true
 * });
 * ```
 * 
 * 2. Application context (clears on navigation):
 * ```tsx
 * useAgenticContext({
 *   applicationKey: 'MY_APP',
 *   assistantKey: 'my-assistant',
 *   clearOnUnmount: true
 * });
 * ```
 * 
 * 3. Contextual control (isolated, clears immediately on unmount):
 * ```tsx
 * useAgenticContext({
 *   featureKey: 'analytics-dashboard',
 *   focusKey: dashboardId,
 *   isContextualControl: true
 * });
 * ```
 * 
 * 4. Persistent context (survives navigation):
 * ```tsx
 * useAgenticContext({
 *   applicationKey: 'MY_APP',
 *   clearOnUnmount: false,
 *   preserveOnRoutes: ['/applications', '/applications/:id']
 * });
 * ```
 */
export function useAgenticContext(config: AgenticContextConfig) {
  const location = useLocation();
  const previousLocationRef = useRef<string>(location.pathname);
  const configRef = useRef(config);
  
  // Update ref when config changes
  configRef.current = config;
  
  // Apply context when component mounts or config changes
  useEffect(() => {
    const currentConfig = configRef.current;
    
    // Skip if this is a contextual control and we're not setting anything
    if (currentConfig.isContextualControl && !currentConfig.featureKey && !currentConfig.applicationKey) {
      return;
    }
    
    console.log(`🎯 [useAgenticContext] Applying context:`, {
      featureKey: currentConfig.featureKey,
      applicationKey: currentConfig.applicationKey,
      focusKey: currentConfig.focusKey,
      isContextualControl: currentConfig.isContextualControl,
    });
    
    // Apply context based on config
    if (currentConfig.featureKey) {
      AgenticFeatureRegistry.setFeatureContext(
        currentConfig.featureKey,
        currentConfig.agentId,
        currentConfig.assistantKey
      );
    } else if (currentConfig.applicationKey) {
      AgenticFeatureRegistry.setApplicationContext(
        currentConfig.applicationKey,
        currentConfig.assistantKey || '',
        currentConfig.agentId
      );
    }
    
    // Set focus key if provided
    if (currentConfig.focusKey) {
      AgenticFeatureRegistry.setFocusKey(currentConfig.focusKey);
    }
  }, [
    config.featureKey,
    config.applicationKey,
    config.assistantKey,
    config.agentId,
    config.focusKey,
    config.isContextualControl,
  ]);
  
  // Handle route changes and cleanup
  useEffect(() => {
    const currentConfig = configRef.current;
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;
    
    // Update previous location
    previousLocationRef.current = currentPath;
    
    // Skip if this is a contextual control (it handles its own cleanup)
    if (currentConfig.isContextualControl) {
      return;
    }
    
    // Check if we should preserve context for this route
    const shouldPreserve = currentConfig.preserveOnRoutes?.some(pattern => {
      // Simple pattern matching (supports :param syntax)
      const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
      return regex.test(currentPath);
    });
    
    // If route changed and we should clear on unmount/navigation
    if (currentPath !== previousPath && currentConfig.clearOnUnmount !== false) {
      // Check if we should preserve based on route patterns
      if (!shouldPreserve) {
        console.log(`🧹 [useAgenticContext] Route changed (${previousPath} → ${currentPath}), clearing context`);
        
        // Clear only the context that this hook set
        if (currentConfig.featureKey) {
          const currentSettings = AgenticFeatureRegistry.getSettings();
          // Only clear if this featureKey is still set (don't clear if another component set a different one)
          if (currentSettings.featureKey === currentConfig.featureKey) {
            AgenticFeatureRegistry.updateSettings({
              featureKey: undefined,
              focusKey: currentConfig.focusKey ? undefined : currentSettings.focusKey, // Preserve focusKey if we didn't set it
            });
          }
        } else if (currentConfig.applicationKey) {
          const currentSettings = AgenticFeatureRegistry.getSettings();
          // Only clear if this applicationKey is still set
          if (currentSettings.applicationKey === currentConfig.applicationKey) {
            AgenticFeatureRegistry.updateSettings({
              applicationKey: undefined,
              assistantKey: undefined,
              focusKey: currentConfig.focusKey ? undefined : currentSettings.focusKey,
            });
          }
        }
        
        // Clear focusKey if we set it
        if (currentConfig.focusKey) {
          AgenticFeatureRegistry.clearFocusKey();
        }
      } else {
        console.log(`✅ [useAgenticContext] Route changed but preserving context for pattern match`);
      }
    }
  }, [location.pathname]);
  
  // Cleanup on unmount (for contextual controls or when clearOnUnmount is true)
  useEffect(() => {
    const currentConfig = configRef.current;
    
    return () => {
      // For contextual controls, always clear on unmount
      if (currentConfig.isContextualControl || currentConfig.clearOnUnmount !== false) {
        console.log(`🧹 [useAgenticContext] Component unmounting, clearing context`);
        
        const currentSettings = AgenticFeatureRegistry.getSettings();
        
        // Clear only what we set
        if (currentConfig.featureKey && currentSettings.featureKey === currentConfig.featureKey) {
          AgenticFeatureRegistry.updateSettings({
            featureKey: undefined,
            focusKey: currentConfig.focusKey ? undefined : currentSettings.focusKey,
          });
        } else if (currentConfig.applicationKey && currentSettings.applicationKey === currentConfig.applicationKey) {
          AgenticFeatureRegistry.updateSettings({
            applicationKey: undefined,
            assistantKey: undefined,
            focusKey: currentConfig.focusKey ? undefined : currentSettings.focusKey,
          });
        }
        
        // Clear focusKey if we set it
        if (currentConfig.focusKey) {
          AgenticFeatureRegistry.clearFocusKey();
        }
      }
    };
  }, []);
}

/**
 * Hook for route-based context management
 * Automatically sets context based on current route
 * 
 * Usage in App.tsx or layout component:
 * ```tsx
 * useRouteBasedAgenticContext({
 *   '/analytics': { featureKey: 'analytics-dashboard' },
 *   '/applications/:id': { applicationKey: (params) => `APP_${params.id}` },
 *   '/reports': { featureKey: 'reports' },
 * });
 * ```
 */
export interface RouteContextMap {
  [routePattern: string]: AgenticContextConfig | ((params: Record<string, string>) => AgenticContextConfig);
}

export function useRouteBasedAgenticContext(routeMap: RouteContextMap) {
  const location = useLocation();
  
  useEffect(() => {
    // Find matching route
    for (const [pattern, configOrFn] of Object.entries(routeMap)) {
      // Extract route params
      const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '([^/]+)') + '$');
      const match = location.pathname.match(regex);
      
      if (match) {
        // Extract params
        const paramNames = pattern.match(/:[^/]+/g)?.map(p => p.substring(1)) || [];
        const params: Record<string, string> = {};
        paramNames.forEach((name, index) => {
          params[name] = match[index + 1];
        });
        
        // Get config (function or object)
        const config = typeof configOrFn === 'function' ? configOrFn(params) : configOrFn;
        
        // Apply context
        if (config.featureKey) {
          AgenticFeatureRegistry.setFeatureContext(
            config.featureKey,
            config.agentId,
            config.assistantKey
          );
        } else if (config.applicationKey) {
          AgenticFeatureRegistry.setApplicationContext(
            config.applicationKey,
            config.assistantKey || '',
            config.agentId
          );
        }
        
        if (config.focusKey) {
          AgenticFeatureRegistry.setFocusKey(config.focusKey);
        }
        
        console.log(`🎯 [useRouteBasedAgenticContext] Applied context for route: ${pattern}`);
        return; // Found match, stop
      }
    }
    
    // No match found - clear context if we're not on a matched route
    console.log(`🧹 [useRouteBasedAgenticContext] No route match, clearing context`);
    AgenticFeatureRegistry.resetContext();
  }, [location.pathname]);
}

